import React from 'react';
import Button from '../../ui/Button';

type Row = Record<string, string>;

type FormItem = { slug: string; title: string };

type FormResponsesData = {
  questions: string[];
  additionalInfoKeys: string[];
  rows: Row[];
};

const FormResponsesPage: React.FC = () => {
  const [forms, setForms] = React.useState<FormItem[]>([]);
  const [selectedSlug, setSelectedSlug] = React.useState('');
  const [data, setData] = React.useState<FormResponsesData | null>(null);
  const [loadingForms, setLoadingForms] = React.useState(true);
  const [loadingResponses, setLoadingResponses] = React.useState(false);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    window
      .fetch('/api/formList')
      .then((r) => r.json())
      .then((json) => setForms(json.forms || []))
      .finally(() => setLoadingForms(false));
  }, []);

  const loadResponses = async (slug: string) => {
    setSelectedSlug(slug);
    setLoadingResponses(true);
    setError('');
    setData(null);
    try {
      const res = await window.fetch(`/api/formResponses?slug=${encodeURIComponent(slug)}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setData(json);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar respostas');
    } finally {
      setLoadingResponses(false);
    }
  };

  const deleteRow = async (id: string) => {
    if (!confirm('Deletar esta resposta?')) return;
    const res = await window.fetch(`/api/formResponseDelete?id=${id}`, { method: 'DELETE' });
    console.log('[delete]', id, res.status);
    if (res.ok) {
      setData((prev) => prev && { ...prev, rows: prev.rows.filter((r) => r._id !== id) });
    } else {
      alert('Erro ao deletar. Verifique o console.');
    }
  };

  const exportCsv = () => {
    window.open(`/api/formResponses?slug=${encodeURIComponent(selectedSlug)}&format=csv`);
  };

  const columns = data ? ['date', 'formTitle', ...data.additionalInfoKeys, ...data.questions] : [];
  const selectedForm = forms.find((f) => f.slug === selectedSlug);

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('pt-BR');
    } catch {
      return iso;
    }
  };

  return (
    <div style={{ display: 'flex', gap: 24, padding: '24px 0', alignItems: 'flex-start' }}>
      {/* Sidebar */}
      <div
        style={{
          minWidth: 220,
          maxWidth: 260,
          borderRight: '1px solid #eee',
          paddingRight: 16,
          flexShrink: 0,
        }}
      >
        <h3 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600, color: '#666' }}>
          FORMULÁRIOS
        </h3>
        {loadingForms ? (
          <p style={{ fontSize: 13, color: '#999' }}>Carregando...</p>
        ) : forms.length === 0 ? (
          <p style={{ fontSize: 13, color: '#999' }}>Nenhum form encontrado.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {forms.map((f) => (
              <li key={f.slug}>
                <button
                  onClick={() => loadResponses(f.slug)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '8px 10px',
                    background: selectedSlug === f.slug ? '#f0f0f0' : 'none',
                    border: 'none',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: selectedSlug === f.slug ? 600 : 400,
                      display: 'block',
                    }}
                  >
                    {f.title || f.slug}
                  </span>
                  <span style={{ fontSize: 11, color: '#999', display: 'block' }}>{f.slug}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedSlug && !loadingForms && (
          <p style={{ color: '#999', fontSize: 14 }}>
            Selecione um formulário para ver as respostas.
          </p>
        )}
        {loadingResponses && <p style={{ color: '#999', fontSize: 14 }}>Carregando respostas...</p>}
        {error && <p style={{ color: 'red', fontSize: 14 }}>{error}</p>}

        {data && !loadingResponses && (
          <>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 14, color: '#666' }}>
                <strong>{selectedForm?.title || selectedSlug}</strong> · {data.rows.length} resposta
                {data.rows.length !== 1 ? 's' : ''}
              </span>
              <Button onClick={exportCsv}>Exportar CSV</Button>
            </div>

            {data.rows.length === 0 ? (
              <p style={{ color: '#999', fontSize: 14 }}>Nenhuma resposta encontrada.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 13, width: '100%' }}>
                  <thead>
                    <tr>
                      {columns.map((col) => (
                        <th key={col} style={thStyle}>
                          {col === 'date' ? 'Data' : col === 'formTitle' ? 'Formulário' : col}
                        </th>
                      ))}
                      <th style={thStyle} />
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row, i) => (
                      <tr key={row._id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        {columns.map((col) => (
                          <td
                            key={col}
                            style={tdStyle}
                            title={col === 'date' ? formatDate(row[col]) : row[col]}
                          >
                            {col === 'date' ? formatDate(row[col]) : row[col] || '—'}
                          </td>
                        ))}
                        <td
                          style={{
                            border: '1px solid #ddd',
                            padding: '4px 8px',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          }}
                        >
                          <button
                            onClick={() => deleteRow(row._id)}
                            title="Deletar"
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: '#c00',
                              fontSize: 14,
                              padding: 0,
                            }}
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '6px 10px',
  background: '#f5f5f5',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  fontWeight: 600,
};

const tdStyle: React.CSSProperties = {
  border: '1px solid #ddd',
  padding: '6px 10px',
  maxWidth: 300,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export default FormResponsesPage;
